import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Cell } from 'recharts';
const PLByBusinessLineDashboard: React.FC = () => {
  const kpis = [{
    title: 'Revenue by Line',
    value: '₹9,250 Cr',
    change: '+5.2%',
    isPositive: true,
    subtitle: 'Total across all lines'
  }, {
    title: 'Gross Margin %',
    value: '28.5%',
    change: '+1.2%',
    isPositive: true,
    subtitle: 'Weighted average'
  }, {
    title: 'EBITDA Contribution %',
    value: '18.3%',
    change: '+0.8%',
    isPositive: true,
    subtitle: 'Overall EBITDA margin'
  }];
  // P&L Comparison Data
  const plComparisonData = [{
    line: 'Lighting',
    revenue: 2620,
    cogs: 1865,
    grossProfit: 755,
    opex: 270,
    ebitda: 485,
    color: '#3b82f6'
  }, {
    line: 'Electrical',
    revenue: 3185,
    cogs: 2267,
    grossProfit: 918,
    opex: 306,
    ebitda: 612,
    color: '#10b981'
  }, {
    line: 'Polymer',
    revenue: 2075,
    cogs: 1496,
    grossProfit: 579,
    opex: 251,
    ebitda: 328,
    color: '#f59e0b'
  }, {
    line: 'Safety',
    revenue: 1370,
    cogs: 986,
    grossProfit: 384,
    opex: 139,
    ebitda: 245,
    color: '#ef4444'
  }];
  // Trend by Business Line (Quarterly)
  const trendData = [{
    quarter: 'Q1 FY24',
    Lighting: 630,
    Electrical: 765,
    Polymer: 500,
    Safety: 330
  }, {
    quarter: 'Q2 FY24',
    Lighting: 645,
    Electrical: 785,
    Polymer: 510,
    Safety: 338
  }, {
    quarter: 'Q3 FY24',
    Lighting: 658,
    Electrical: 802,
    Polymer: 520,
    Safety: 345
  }, {
    quarter: 'Q4 FY24',
    Lighting: 687,
    Electrical: 833,
    Polymer: 545,
    Safety: 357
  }];
  // Contribution Margin Data
  const contributionMarginData = [{
    line: 'Lighting',
    contributionMargin: 28.8,
    ebitdaMargin: 18.5
  }, {
    line: 'Electrical',
    contributionMargin: 28.8,
    ebitdaMargin: 19.2
  }, {
    line: 'Polymer',
    contributionMargin: 27.9,
    ebitdaMargin: 15.8
  }, {
    line: 'Safety',
    contributionMargin: 28.0,
    ebitdaMargin: 17.9
  }];
  // Full P&L Table Data
  const plTableData = [{
    line: 'Lighting',
    revenue: 2620,
    cogs: 1865,
    grossProfit: 755,
    grossMargin: 28.8,
    opex: 270,
    ebitda: 485,
    ebitdaMargin: 18.5,
    depreciation: 95,
    ebit: 390,
    interest: 42,
    pbt: 348,
    tax: 87,
    pat: 261,
    patMargin: 10.0,
    color: '#3b82f6'
  }, {
    line: 'Electrical',
    revenue: 3185,
    cogs: 2267,
    grossProfit: 918,
    grossMargin: 28.8,
    opex: 306,
    ebitda: 612,
    ebitdaMargin: 19.2,
    depreciation: 118,
    ebit: 494,
    interest: 51,
    pbt: 443,
    tax: 111,
    pat: 332,
    patMargin: 10.4,
    color: '#10b981'
  }, {
    line: 'Polymer',
    revenue: 2075,
    cogs: 1496,
    grossProfit: 579,
    grossMargin: 27.9,
    opex: 251,
    ebitda: 328,
    ebitdaMargin: 15.8,
    depreciation: 77,
    ebit: 251,
    interest: 33,
    pbt: 218,
    tax: 55,
    pat: 163,
    patMargin: 7.9,
    color: '#f59e0b'
  }, {
    line: 'Safety',
    revenue: 1370,
    cogs: 986,
    grossProfit: 384,
    grossMargin: 28.0,
    opex: 139,
    ebitda: 245,
    ebitdaMargin: 17.9,
    depreciation: 51,
    ebit: 194,
    interest: 18,
    pbt: 176,
    tax: 44,
    pat: 132,
    patMargin: 9.6,
    color: '#ef4444'
  }];
  return <div data-id="element-4334">
      <DescriptionPanel title="P&L by Business Line" description="Detailed profit and loss analysis across all business lines. Compare revenue, margins, and profitability metrics to identify top performers and optimization opportunities." insights={['Electrical line leads with ₹3,185 Cr revenue and highest EBITDA margin (19.2%)', 'All lines maintain healthy gross margins above 27.9%', 'Polymer line shows margin improvement opportunity - currently at 15.8% EBITDA', 'Safety line delivers strong profitability despite smaller scale']} data-id="element-4335" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-4336">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-4337">
          Business Line Performance Overview
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-4338">
          {kpis.map((kpi, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-4339">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-4340">
                {kpi.title}
              </div>
              <div className="text-xs text-gray-400 mb-3" data-id="element-4341">{kpi.subtitle}</div>
              <div className="text-4xl font-bold text-gray-900 mb-3" data-id="element-4342">
                {kpi.value}
              </div>
              <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-4343">
                {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-4344" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-4345" />}
                {kpi.change} vs last year
              </div>
            </Card>)}
        </div>
      </div>
      {/* P&L Comparison Chart */}
      <Card className="p-6 mb-8" data-id="element-4346">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-4347">
          P&L Comparison by Business Line
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-4348">
          Revenue to EBITDA breakdown (₹ Cr)
        </p>
        <ResponsiveContainer width="100%" height={400} data-id="element-4349">
          <BarChart data={plComparisonData} data-id="element-4350">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-4351" />
            <XAxis dataKey="line" tick={{
            fontSize: 12
          }} data-id="element-4352" />
            <YAxis tick={{
            fontSize: 12
          }} data-id="element-4353" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} formatter={(value: number) => `₹${value} Cr`} data-id="element-4354" />
            <Legend data-id="element-4355" />
            <Bar dataKey="revenue" fill="#3b82f6" name="Revenue" data-id="element-4356" />
            <Bar dataKey="cogs" fill="#ef4444" name="COGS" data-id="element-4357" />
            <Bar dataKey="grossProfit" fill="#10b981" name="Gross Profit" data-id="element-4358" />
            <Bar dataKey="opex" fill="#f59e0b" name="OPEX" data-id="element-4359" />
            <Bar dataKey="ebitda" fill="#8b5cf6" name="EBITDA" data-id="element-4360" />
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-4361">
          <div className="grid grid-cols-4 gap-4" data-id="element-4362">
            {plComparisonData.map((item, index) => <div key={index} className="text-center" data-id="element-4363">
                <div className="text-xs font-semibold mb-1" style={{
              color: item.color
            }} data-id="element-4364">
                  {item.line}
                </div>
                <div className="text-lg font-bold text-gray-900" data-id="element-4365">
                  ₹{item.ebitda} Cr
                </div>
                <div className="text-xs text-gray-600" data-id="element-4366">
                  {(item.ebitda / item.revenue * 100).toFixed(1)}% margin
                </div>
              </div>)}
          </div>
        </div>
      </Card>
      {/* Trend and Contribution Margin */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-4367">
        {/* Revenue Trend by Business Line */}
        <Card className="p-6" data-id="element-4368">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-4369">
            Revenue Trend by Business Line
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-4370">
            Quarterly revenue performance (₹ Cr)
          </p>
          <ResponsiveContainer width="100%" height={300} data-id="element-4371">
            <LineChart data={trendData} data-id="element-4372">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-4373" />
              <XAxis dataKey="quarter" tick={{
              fontSize: 11
            }} data-id="element-4374" />
              <YAxis tick={{
              fontSize: 12
            }} data-id="element-4375" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number) => `₹${value} Cr`} data-id="element-4376" />
              <Legend data-id="element-4377" />
              <Line type="monotone" dataKey="Lighting" stroke="#3b82f6" strokeWidth={3} name="Lighting" dot={{
              r: 4
            }} data-id="element-4378" />
              <Line type="monotone" dataKey="Electrical" stroke="#10b981" strokeWidth={3} name="Electrical" dot={{
              r: 4
            }} data-id="element-4379" />
              <Line type="monotone" dataKey="Polymer" stroke="#f59e0b" strokeWidth={3} name="Polymer" dot={{
              r: 4
            }} data-id="element-4380" />
              <Line type="monotone" dataKey="Safety" stroke="#ef4444" strokeWidth={3} name="Safety" dot={{
              r: 4
            }} data-id="element-4381" />
            </LineChart>
          </ResponsiveContainer>
        </Card>
        {/* Contribution Margin by Line */}
        <Card className="p-6" data-id="element-4382">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-4383">
            Contribution Margin by Line
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-4384">
            Gross margin vs EBITDA margin (%)
          </p>
          <ResponsiveContainer width="100%" height={300} data-id="element-4385">
            <ComposedChart data={contributionMarginData} data-id="element-4386">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-4387" />
              <XAxis dataKey="line" tick={{
              fontSize: 11
            }} data-id="element-4388" />
              <YAxis tick={{
              fontSize: 12
            }} domain={[0, 35]} data-id="element-4389" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number) => `${value.toFixed(1)}%`} data-id="element-4390" />
              <Legend data-id="element-4391" />
              <Bar dataKey="contributionMargin" fill="#3b82f6" name="Contribution Margin %" data-id="element-4392" />
              <Line type="monotone" dataKey="ebitdaMargin" stroke="#10b981" strokeWidth={3} name="EBITDA Margin %" dot={{
              r: 5
            }} data-id="element-4393" />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-4394">
            <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-4395">
              <div className="text-center" data-id="element-4396">
                <div className="text-gray-600" data-id="element-4397">Avg Contribution Margin</div>
                <div className="text-xl font-bold text-blue-600" data-id="element-4398">28.4%</div>
              </div>
              <div className="text-center" data-id="element-4399">
                <div className="text-gray-600" data-id="element-4400">Avg EBITDA Margin</div>
                <div className="text-xl font-bold text-green-600" data-id="element-4401">17.9%</div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* Full P&L Table per Line */}
      <Card className="p-6" data-id="element-4402">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-4403">
          Full P&L Statement by Business Line - FY24
        </h3>
        <div className="overflow-x-auto" data-id="element-4404">
          <table className="w-full" data-id="element-4405">
            <thead data-id="element-4406">
              <tr className="border-b-2 border-gray-300" data-id="element-4407">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4408">
                  Business Line
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4409">
                  Revenue
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4410">
                  COGS
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4411">
                  Gross Profit
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4412">
                  Gross %
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4413">
                  OPEX
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4414">
                  EBITDA
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4415">
                  EBITDA %
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4416">
                  D&A
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4417">
                  EBIT
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4418">
                  Interest
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4419">
                  PBT
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4420">
                  Tax
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4421">
                  PAT
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4422">
                  PAT %
                </th>
              </tr>
            </thead>
            <tbody data-id="element-4423">
              {plTableData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-4424">
                  <td className="py-3 px-4" data-id="element-4425">
                    <div className="flex items-center" data-id="element-4426">
                      <div className="w-3 h-3 rounded mr-2" style={{
                    backgroundColor: row.color
                  }} data-id="element-4427" />
                      <span className="text-sm font-medium text-gray-800" data-id="element-4428">
                        {row.line}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-gray-900" data-id="element-4429">
                    ₹{row.revenue} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-red-600" data-id="element-4430">
                    ₹{row.cogs} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-medium text-blue-600" data-id="element-4431">
                    ₹{row.grossProfit} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-blue-600" data-id="element-4432">
                    {row.grossMargin}%
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-orange-600" data-id="element-4433">
                    ₹{row.opex} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-medium text-green-600" data-id="element-4434">
                    ₹{row.ebitda} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-green-600" data-id="element-4435">
                    {row.ebitdaMargin}%
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-600" data-id="element-4436">
                    ₹{row.depreciation} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-800" data-id="element-4437">
                    ₹{row.ebit} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-600" data-id="element-4438">
                    ₹{row.interest} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-800" data-id="element-4439">
                    ₹{row.pbt} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-600" data-id="element-4440">
                    ₹{row.tax} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-medium text-purple-600" data-id="element-4441">
                    ₹{row.pat} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-purple-600" data-id="element-4442">
                    {row.patMargin}%
                  </td>
                </tr>)}
              {/* Total Row */}
              <tr className="bg-gray-100 border-t-2 border-gray-300 font-bold" data-id="element-4443">
                <td className="py-3 px-4 text-sm text-gray-900" data-id="element-4444">TOTAL</td>
                <td className="py-3 px-4 text-sm text-right text-gray-900" data-id="element-4445">
                  ₹9,250 Cr
                </td>
                <td className="py-3 px-4 text-sm text-right text-red-600" data-id="element-4446">
                  ₹6,614 Cr
                </td>
                <td className="py-3 px-4 text-sm text-right text-blue-600" data-id="element-4447">
                  ₹2,636 Cr
                </td>
                <td className="py-3 px-4 text-sm text-right text-blue-600" data-id="element-4448">
                  28.5%
                </td>
                <td className="py-3 px-4 text-sm text-right text-orange-600" data-id="element-4449">
                  ₹966 Cr
                </td>
                <td className="py-3 px-4 text-sm text-right text-green-600" data-id="element-4450">
                  ₹1,670 Cr
                </td>
                <td className="py-3 px-4 text-sm text-right text-green-600" data-id="element-4451">
                  18.1%
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-4452">
                  ₹341 Cr
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-900" data-id="element-4453">
                  ₹1,329 Cr
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-4454">
                  ₹144 Cr
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-900" data-id="element-4455">
                  ₹1,185 Cr
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-4456">
                  ₹297 Cr
                </td>
                <td className="py-3 px-4 text-sm text-right text-purple-600" data-id="element-4457">
                  ₹888 Cr
                </td>
                <td className="py-3 px-4 text-sm text-right text-purple-600" data-id="element-4458">
                  9.6%
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-4459">
          <div className="grid grid-cols-5 gap-4 text-sm" data-id="element-4460">
            <div className="text-center" data-id="element-4461">
              <div className="text-gray-600" data-id="element-4462">Total Revenue</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-4463">₹9,250 Cr</div>
            </div>
            <div className="text-center" data-id="element-4464">
              <div className="text-gray-600" data-id="element-4465">Total Gross Profit</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-4466">₹2,636 Cr</div>
            </div>
            <div className="text-center" data-id="element-4467">
              <div className="text-gray-600" data-id="element-4468">Total EBITDA</div>
              <div className="text-xl font-bold text-green-600" data-id="element-4469">₹1,670 Cr</div>
            </div>
            <div className="text-center" data-id="element-4470">
              <div className="text-gray-600" data-id="element-4471">Total PAT</div>
              <div className="text-xl font-bold text-purple-600" data-id="element-4472">₹888 Cr</div>
            </div>
            <div className="text-center" data-id="element-4473">
              <div className="text-gray-600" data-id="element-4474">Best Performer</div>
              <div className="text-xl font-bold text-green-600" data-id="element-4475">Electrical</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default PLByBusinessLineDashboard;